import React, { useEffect, useState } from "react";
import {
    clientsCompanyResource,
    plansActivate,
    plansResource,
} from "../../helpers/configRoutes/backend_helper";
import ConfirmDeleteModal from "../../components/modal/ConfirmDeleteModal";
import CustomButtonOval from "../../components/form/CustomButtonOval";
import FiltersPagination from "../../components/elements/FiltersPagination";
import { currencyUSD } from "../../helpers/forms/formater";
import { useSelector } from "react-redux";
import ConfirmPayment from "./ConfirmPayment";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Subscription = () => {
    const [modalConfirm, setModalConfirm] = useState({
        show: false,
        textLoad: "Processing",
    });

    const [filters, setFilters] = useState({
        perPage: 3,
        page: 1,
        search: "",
    });
    const [meta, setMeta] = useState({ total: 0, current_page: 0 });
    const [plans, setPlans] = useState([]);
    const [company, setCompany] = useState({});
    const user = useSelector((state) => state.Login.user);

    useEffect(() => {
        const getPlans = async () => {
            const response = await plansResource("get", { params: filters });
            setPlans(response.plans || []);
            setMeta(response.meta || { total: 0, current_page: 1 });
        };
        getPlans();
    }, [filters]);

    useEffect(() => {
        const getDataCompany = async () => {
            if (!user?.company_id) return;
            const response = await clientsCompanyResource(
                "show",
                {},
                user.company_id
            );
            if (response.status && response.company) {
                setCompany(response.company);
            }
        };
        getDataCompany();
    }, [user?.company_id]);

    const deletePlan = async () => {
        setModalConfirm({ ...modalConfirm, loading: true });
        const response = await plansResource("del", {}, modalConfirm.id);
        if (response.status) {
            setFilters({ ...filters });
        }
        setModalConfirm({
            ...modalConfirm,
            loading: false,
            show: false,
            id: null,
        });
    };

    const activate = async (plan) => {
        const response = await plansActivate(plan);
        if (response.status) {
            setFilters({ ...filters });
        }
    };

    const isCurrentPlan = (plan) => company.plan_id === plan.id;

    const getActionLabel = (plan) => {
        if (isCurrentPlan(plan)) return "Current plan";
        if (!company.plan_id) return "Select plan";
        return company.plan_id < plan.id ? "Upgrade" : "Downgrade";
    };

    return (
        <>
            <div className="min-h-screen bg-cs-bg-alt py-8 px-4">
                <div className="max-w-auto mx-auto space-y-6">
                    {/* HEADER */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <p className="text-sm uppercase tracking-[0.18em] text-cs-muted">
                                Subscription & billing
                            </p>
                            <h1 className="text-2xl font-semibold text-cs-dark">
                                Choose the right plan for your fleet
                            </h1>
                            <p className="text-sm text-cs-muted mt-1">
                                Compare plans, limits and pricing. Your current
                                subscription is highlighted below.
                            </p>
                        </div>
                        <div className="flex flex-col items-start md:items-end gap-1 text-sm text-cs-muted">
                            <span>
                                Company:{" "}
                                <span className="font-medium text-cs-primary-dark">
                                    {company.name || "Not configured"}
                                </span>
                            </span>
                            {company.plan_id && (
                                <span>
                                    Active plan ID:{" "}
                                    <span className="font-medium text-cs-primary-dark">
                                        {company.plan_id}
                                    </span>
                                </span>
                            )}
                        </div>
                    </div>

                    {/* PLANS GRID */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {plans.map((plan) => {
                            const price = Number(plan.price || 0);
                            const taxes =
                                price === 0
                                    ? 0
                                    : price *
                                    (Number(plan.taxes || 0) / 100);
                            const total = price + taxes;
                            const current = isCurrentPlan(plan);
                            const actionLabel = getActionLabel(plan);

                            return (
                                <div
                                    key={plan.id}
                                    className={`
                                        relative flex flex-col h-full
                                        rounded-2xl border
                                        p-5
                                        transition-all
                                        ${current
                                            ? "bg-cs-primary-soft border-cs-primary shadow-md shadow-cs-primary/15"
                                            : "bg-cs-bg border-cs-border shadow-sm hover:shadow-md hover:border-cs-primary-soft"
                                        }
                                    `}
                                >
                                    {/* Badge top-right */}
                                    {current && (
                                        <div className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-cs-primary text-white text-sm font-medium shadow-sm">
                                            Current subscription
                                        </div>
                                    )}

                                    {/* Plan name & price */}
                                    <div className="mb-3">
                                        <h3 className="text-sm font-semibold text-cs-dark">
                                            {plan.name}
                                        </h3>
                                        <div className="flex items-baseline gap-1 mt-1">
                                            <span className="text-2xl font-bold text-cs-primary">
                                                {currencyUSD(price)}
                                            </span>
                                            <span className="text-sm text-cs-muted">
                                                / month
                                            </span>
                                        </div>
                                        {price === 0 && (
                                            <p className="text-sm text-cs-success mt-0.5">
                                                Trial / Free tier
                                            </p>
                                        )}
                                    </div>

                                    <div className="h-px bg-cs-border/60 my-2" />

                                    {/* Details */}
                                    <div className="flex-1 mt-1 space-y-1.5 text-sm text-cs-muted">
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Base:
                                            </span>{" "}
                                            {currencyUSD(price)} USD
                                        </p>
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Taxes:
                                            </span>{" "}
                                            {currencyUSD(
                                                price === 0 ? 0 : taxes
                                            )}
                                        </p>
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Est. total:
                                            </span>{" "}
                                            {currencyUSD(
                                                price === 0 ? 0 : total
                                            )}{" "}
                                            / month
                                        </p>
                                        <p className="mt-1">
                                            <span className="font-medium text-cs-dark">
                                                Trucks included:
                                            </span>{" "}
                                            {plan.trucks}
                                        </p>
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Users included:
                                            </span>{" "}
                                            {plan.users}
                                        </p>
                                    </div>

                                    <div className="h-px bg-cs-border/60 my-3" />

                                    {/* Action button */}
                                    <div className="mt-auto flex flex-col items-center gap-1">
                                        <CustomButtonOval
                                            onClick={() =>
                                                !current &&
                                                plan?.can?.changeSubs &&
                                                setModalConfirm({ ...modalConfirm, show: true, plan: plan })
                                            }
                                            isDisabled={
                                                !plan?.can?.changeSubs || current
                                            }
                                            className={`
                                                w-full text-[10px] font-medium
                                                ${current
                                                    ? "bg-cs-primary text-white cursor-default"
                                                    : plan?.can?.changeSubs
                                                        ? "bg-cs-bg text-cs-primary border border-cs-primary hover:bg-cs-primary hover:text-white transition-all"
                                                        : "bg-cs-bg-alt text-cs-muted border border-cs-border cursor-not-allowed"
                                                }
                                            `}
                                        >
                                            {actionLabel}
                                        </CustomButtonOval>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination (sin filtros visuales aquí) */}
                    <FiltersPagination
                        filters={filters}
                        setFilters={setFilters}
                        showFilters={false}
                        meta={meta}
                    />
                </div>
            </div>
            {modalConfirm.show &&
                <Elements stripe={stripePromise}>
                    <ConfirmPayment
                        modal={modalConfirm}
                        setModal={setModalConfirm}
                    />
                </Elements>
            }
        </>
    );
};

export default Subscription;
